import React, { useState } from 'react';
import { FileText, Plus, User, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TestClientBadge, DnqBadge, ParaTrialBadge } from '../ClientNameWithBadge';
import { isParaTrialClient } from '../../utils/allocationVisibility';
import { getClientDisplayNameFor, useClientStoreVersion } from '../../services/clientStateService';
import { MAX_WORKSPACE_TABS } from '../../hooks/useWorkspaceTabs';

const CHIP_BASE = 'group/tab relative isolate inline-flex h-8 shrink-0 select-none items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors';
const CHIP_ACTIVE = 'border-[color:color-mix(in_oklch,var(--secondary-foreground)_35%,transparent)] bg-secondary text-secondary-foreground';
const CHIP_IDLE = 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground';

/** What a tab shows: its client (when one is open) and its page. */
export function describeWorkspaceTab(tab, scope, pageLabelFor) {
  const client = scope?.selectedClient || null;
  const pageLabel = pageLabelFor(tab.page);
  const clientName = client ? getClientDisplayNameFor(client) : null;
  return {
    client,
    clientName,
    pageLabel,
    title: clientName ? `${clientName} · ${pageLabel}` : pageLabel,
  };
}

/**
 * Header tabs (desktop): one chip per workspace tab, labelled with its client
 * (plus Test / DNQ markers) and page. Click to switch, X to close, drag to
 * reorder, + for a new tab. Clicking the active tab's client name finds the
 * client in the portal.
 */
export default function WorkspaceTabStrip({
  tabs,
  activeId,
  getScope,
  pageLabelFor,
  canOpenTab,
  onSwitch,
  onClose,
  onNew,
  onReorder,
  onActiveClientClick,
  /** A tab just opened in the background: its chip pulses briefly. */
  justOpenedId = null,
}) {
  useClientStoreVersion(); // chips follow client changes in background tabs too
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);
  const compact = tabs.length > 3;

  return (
    <div className="flex min-w-0 items-center gap-1" role="tablist" aria-label="Open tabs">
      <div className="flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          const info = describeWorkspaceTab(tab, getScope(tab.id), pageLabelFor);
          const Icon = info.client ? User : FileText;
          return (
            <div
              key={tab.id}
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              title={info.title}
              draggable={tabs.length > 1}
              onDragStart={(e) => {
                setDragId(tab.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', tab.id);
              }}
              onDragOver={(e) => {
                if (!dragId || dragId === tab.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDropId(tab.id);
              }}
              onDragLeave={() => setDropId((cur) => (cur === tab.id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId) onReorder(dragId, tab.id);
                setDragId(null);
                setDropId(null);
              }}
              onDragEnd={() => { setDragId(null); setDropId(null); }}
              onClick={() => { if (!active) onSwitch(tab.id); }}
              onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); onClose(tab.id); } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSwitch(tab.id); }
              }}
              className={cn(
                CHIP_BASE,
                active ? CHIP_ACTIVE : CHIP_IDLE,
                !active && 'cursor-pointer',
                dragId === tab.id && 'opacity-50',
                // Drawn inside the chip: the scrolling strip clips anything outside it.
                dropId === tab.id && 'ring-2 ring-inset ring-ring',
                justOpenedId === tab.id && 'bg-primary/10 text-foreground',
              )}
            >
              {active && info.client && onActiveClientClick ? (
                // The hover tint and focus ring are drawn on a layer that fills the whole
                // chip (up to its border), behind the label and above the chip background.
                <button
                  type="button"
                  onClick={onActiveClientClick}
                  className="flex min-w-0 items-center gap-1.5 focus:outline-none before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius)-3px)] before:content-[''] hover:before:bg-muted-foreground/20 focus-visible:before:ring-2 focus-visible:before:ring-inset focus-visible:before:ring-ring"
                  aria-label="Find client in portal"
                  title="Find client in portal"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <TabLabel info={info} compact={compact} />
                </button>
              ) : (
                <>
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <TabLabel info={info} compact={compact} />
                </>
              )}
              {info.client?.isTestClient && <TestClientBadge />}
              {info.client?.isDnq === true && <DnqBadge />}
              {isParaTrialClient(info.client) && <ParaTrialBadge />}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(tab.id); }}
                className={cn(
                  "relative rounded-full p-0.5 hover:bg-muted-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset after:absolute after:-inset-2 after:content-[''] md:after:hidden",
                  !active && 'opacity-60 group-hover/tab:opacity-100',
                )}
                aria-label={`Close tab ${info.title}`}
                title="Close tab (Alt+W)"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onNew}
        disabled={!canOpenTab}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="New tab"
        title={canOpenTab ? 'New tab (Alt+T)' : `Close a tab to open another (max ${MAX_WORKSPACE_TABS})`}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function TabLabel({ info, compact }) {
  if (!info.clientName) {
    return <span className={cn('truncate', compact ? 'max-w-[6rem]' : 'max-w-[10rem]')}>{info.pageLabel}</span>;
  }
  return (
    <span className="flex min-w-0 items-baseline gap-1">
      <span className={cn('truncate', compact ? 'max-w-[6rem]' : 'max-w-[9rem]')}>{info.clientName}</span>
      {!compact && (
        <span className="truncate max-w-[6rem] text-[11px] font-normal opacity-70">· {info.pageLabel}</span>
      )}
    </span>
  );
}
